import { apiChatFetch } from '../index';
import type {
  ChatMessage,
  CheckAttachmentsAvailabilityBody,
  CheckAttachmentsAvailabilityResponse,
  CreateThreadBody,
  EditMessageVars,
  GetReplyThreadParams,
  ReplyThreadBody,
  ReplyThreadResponse,
  RequestFileUploadBody,
  RequestFileUploadResponse,
  SendMessageVars,
  Thread,
  ThreadIdParams,
} from './types';
import type {
  AddThreadParticipantsVars,
  CreateTopicBody,
  LeaveThreadVars,
  MarkContextReadVars,
  RemoveThreadParticipantVars,
  RenameThreadVars,
  SetThreadFilingsVars,
  SetThreadParticipantRoleVars,
  SetThreadTopicsVars,
  SetTopicVisibilityVars,
  Topic,
  TopicIdParams,
  UpdateTopicVars,
} from './v2-types';

export const _createThread = (body: CreateThreadBody) => {
  return apiChatFetch<Thread>('/threads', {
    method: 'POST',
    body,
  });
};

export const _readThread = (threadId: string) => {
  return apiChatFetch(`/threads/${threadId}/read`, {
    method: 'PUT',
  });
};

export const _archiveThread = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch(`/threads/${threadId}/archive`, {
    method: 'PUT',
  });
};

export const _unarchiveThread = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch(`/threads/${threadId}/unarchive`, {
    method: 'PUT',
  });
};

export const _replyThread = ({ threadId, ...body }: ReplyThreadBody) => {
  return apiChatFetch<ReplyThreadResponse>(`/threads/${threadId}/reply`, {
    method: 'POST',
    body,
  });
};

export const _getReplyThread = ({ threadId, messageId }: GetReplyThreadParams) => {
  return apiChatFetch<{ threadId: string | null }>(`/threads/${threadId}/reply-thread`, {
    method: 'GET',
    ...(messageId ? { params: { messageId } } : {}),
  });
};
export const _requestFileUpload = ({ threadId, ...body }: RequestFileUploadBody) => {
  return apiChatFetch<RequestFileUploadResponse>(`/threads/${threadId}/file-upload-request`, {
    method: 'POST',
    body,
  });
};

export const _checkAttachmentsAvailability = (body: CheckAttachmentsAvailabilityBody) => {
  return apiChatFetch<CheckAttachmentsAvailabilityResponse>(
    '/threads/attachments/check-availability',
    {
      method: 'POST',
      body,
    },
  );
};

export const _sendMessage = ({ threadId, ...body }: SendMessageVars) => {
  return apiChatFetch<ChatMessage>(`/threads/${threadId}/messages`, {
    method: 'POST',
    body,
  });
};

export const _editMessage = ({ messageId, content }: EditMessageVars) => {
  return apiChatFetch<ChatMessage>(`/messages/${messageId}`, {
    method: 'PUT',
    body: { content },
  });
};

export const _deleteMessage = (messageId: string) => {
  return apiChatFetch<void>(`/messages/${messageId}`, { method: 'DELETE' });
};

export const _markContextRead = ({ contextId, upTo }: MarkContextReadVars) => {
  return apiChatFetch<void>(`/contexts/${contextId}/read`, {
    method: 'PUT',
    body: { upTo },
  });
};

export const _createTopic = (body: CreateTopicBody) => {
  return apiChatFetch<Topic>('/topics', {
    method: 'POST',
    body,
  });
};

export const _updateTopic = ({ topicId, ...body }: UpdateTopicVars) => {
  return apiChatFetch<Topic>(`/topics/${topicId}`, {
    method: 'PATCH',
    body,
  });
};

export const _archiveTopic = ({ topicId }: TopicIdParams) => {
  return apiChatFetch<void>(`/topics/${topicId}/archive`, { method: 'PUT' });
};

export const _restoreTopic = ({ topicId }: TopicIdParams) => {
  return apiChatFetch<void>(`/topics/${topicId}/restore`, { method: 'PUT' });
};

export const _setTopicVisibility = ({ topicId, roleIds }: SetTopicVisibilityVars) => {
  return apiChatFetch<void>(`/topics/${topicId}/visibility`, {
    method: 'PUT',
    body: { roleIds },
  });
};

export const _setThreadTopics = ({ threadId, topicIds }: SetThreadTopicsVars) => {
  return apiChatFetch<void>(`/threads/${threadId}/topics`, {
    method: 'PUT',
    body: { topicIds },
  });
};

export const _setThreadFilings = ({ threadId, contextIds }: SetThreadFilingsVars) => {
  return apiChatFetch<void>(`/threads/${threadId}/filings`, {
    method: 'PUT',
    body: { contextIds },
  });
};

export const _muteThread = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/mute`, { method: 'PUT' });
};

export const _unmuteThread = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/unmute`, { method: 'PUT' });
};

export const _markWaiting = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/waiting`, { method: 'PUT' });
};

export const _unmarkWaiting = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/unwaiting`, { method: 'PUT' });
};

export const _resolveThread = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/resolve`, { method: 'PUT' });
};

export const _unresolveThread = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/unresolve`, { method: 'PUT' });
};

export const _markPrivate = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/private`, { method: 'PUT' });
};

export const _markVisible = ({ threadId }: ThreadIdParams) => {
  return apiChatFetch<void>(`/threads/${threadId}/visible`, { method: 'PUT' });
};

export const _renameThread = ({ threadId, name }: RenameThreadVars) => {
  return apiChatFetch<Thread>(`/threads/${threadId}`, {
    method: 'PATCH',
    body: { name },
  });
};

export const _addThreadParticipants = ({ threadId, recipients }: AddThreadParticipantsVars) => {
  return apiChatFetch<void>(`/threads/${threadId}/participants`, {
    method: 'POST',
    body: { recipients },
  });
};

export const _removeThreadParticipant = ({
  threadId,
  participantId,
  kind,
}: RemoveThreadParticipantVars) => {
  return apiChatFetch<void>(
    `/threads/${threadId}/participants/${participantId}?kind=${encodeURIComponent(kind)}`,
    {
      method: 'DELETE',
    },
  );
};

export const _setThreadParticipantRole = ({
  threadId,
  participantId,
  kind,
  role,
}: SetThreadParticipantRoleVars) => {
  return apiChatFetch<void>(`/threads/${threadId}/participants/${participantId}/role`, {
    method: 'PUT',
    body: { kind, role },
  });
};

export const _leaveThread = ({ threadId }: LeaveThreadVars) => {
  return apiChatFetch<void>(`/threads/${threadId}/leave`, { method: 'POST' });
};
